<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useDataViewStore } from '../../stores/dataViewStore';
import { useReferenceUnitStore } from '../../stores/referenceUnitStore';
import { useMatterStore } from '../../stores/matterStore';
import { useReferenceSelectionStore } from '../../stores/referenceSelectionStore';
import { useDialoguePreferencesStore } from '../../stores/dialoguePreferencesStore';
import ReferenceViewContainer from './ReferenceViewContainer.vue';
import BulkActionsBar from './BulkActionsBar.vue';

interface ReferencesData {
    references: Record<string, unknown[]>;
    answer?: string;
    question?: string;
    plan_steps?: unknown;
}

const { t } = useI18n();

defineProps<{
    // Whether the panel is currently taking the full content width. Drives the
    // header split icon's pressed state (the toggle itself lives in MatterPage,
    // which owns the layout).
    isFullWidth?: boolean;
    // Hide the split icon when full-width isn't available (e.g. the raw drawer
    // is open and owns the layout).
    canToggleFullWidth?: boolean;
    // Show a collapse (to strip) control instead — used when the raw drawer is
    // open, where full-width is unavailable but collapsing references still is.
    canCollapse?: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'collapse'): void;
    (e: 'toggle-full-width'): void;
}>();

const dataViewStore = useDataViewStore();
const referenceUnitStore = useReferenceUnitStore();
const matterStore = useMatterStore();
const referenceSelectionStore = useReferenceSelectionStore();
const dialoguePreferencesStore = useDialoguePreferencesStore();

const selectedChat = computed(() => dataViewStore.selectedChatForDetails);

// RecordId of the chat the "record viewer" auto-open has already fired for,
// so it runs at most once per pill-open (not again on type/view switches).
const autoOpenedChatId = ref<string | null>(null);

const referencesData = ref<ReferencesData | null>(null);
// Defaults to true so the panel shows the loading state on first paint —
// otherwise we'd flash the empty "No references" message during the initial
// metadata + references load that the immediate selectedChat watcher kicks off.
const referencesLoading = ref(true);
const referencesError = ref<string | null>(null);

// Type and view live in the data-view store so the URL sync composable can
// drive them on cold deep links. Aliased to keep the existing call sites
// short.
const { selectedReferenceType: selectedType, selectedReferenceUnit } = storeToRefs(dataViewStore);
const isViewDropdownOpen = ref(false);

const selectType = (typeName: string) => {
    selectedType.value = typeName;
    dataViewStore.showDetailView = false;
    dataViewStore.communicationGroups = [];
    dataViewStore.currentGroupIndex = 0;
};

const toggleViewDropdown = () => {
    isViewDropdownOpen.value = !isViewDropdownOpen.value;
};

const selectView = (viewName: string) => {
    selectedReferenceUnit.value = viewName;
    isViewDropdownOpen.value = false;
    dataViewStore.showDetailView = false;
    dataViewStore.communicationGroups = [];
    dataViewStore.currentGroupIndex = 0;
};

const getViewLabel = (ru: { description?: string; name: string } | undefined) =>
    ru ? ru.description || ru.name : '';

const getViewIcon = (ru: { description?: string; name?: string } | null | undefined) => {
    if (!ru) return 'view_agenda';
    const desc = (ru.description || ru.name || '').toLowerCase();
    if (desc.includes('day')) return 'today';
    return 'forum';
};

const dynamicTypeTabs = computed(() => {
    if (!referencesData.value || !referencesData.value.references) {
        return [];
    }
    return Object.keys(referencesData.value.references);
});

const typeReferenceCounts = computed(() => {
    if (!referencesData.value || !referencesData.value.references) {
        return {} as Record<string, number>;
    }
    const counts: Record<string, number> = {};
    for (const [typeName, refs] of Object.entries(referencesData.value.references)) {
        counts[typeName] = Array.isArray(refs) ? refs.length : 0;
    }
    return counts;
});

// Show the type chips whenever there's at least one type. A single-type
// result still renders its one chip (matching the design) rather than hiding
// the row entirely.
const showTypeSelector = computed(() => dynamicTypeTabs.value.length >= 1);

// Total references across all types — the count badge next to the title.
const totalReferenceCount = computed(() =>
    Object.values(typeReferenceCounts.value).reduce((sum, n) => sum + n, 0),
);

const availableReferenceUnits = computed(() => {
    if (!selectedType.value) return [];
    return referenceUnitStore.getAvailableReferenceUnits(selectedType.value);
});

// Seed the panes from a references payload and resolve the active type/view.
// Shared by the cache-hit and freshly-fetched paths so they never drift.
const applyReferences = (data: ReferencesData) => {
    referencesData.value = data;

    const types = Object.keys(data?.references || {});
    if (types.length > 0 && (!selectedType.value || !types.includes(selectedType.value))) {
        selectedType.value = types[0];
        console.log('[ReferencesPanel] Auto-selected type:', types[0]);
    }

    // Belt-and-suspenders default for selectedReferenceUnit. The
    // on-type-change watcher below covers user picks, but a cold deep
    // link with ?type=X and no ?view= reaches this point with view
    // still null; this fills it in once metadata is loaded.
    if (selectedType.value) {
        const units = referenceUnitStore.getAvailableReferenceUnits(selectedType.value);
        if (units.length > 0 && !units.some((u) => u.name === selectedReferenceUnit.value)) {
            selectedReferenceUnit.value = units[0].name;
        }
    }

    referencesLoading.value = false;

    maybeAutoOpenRecordViewer(data);
};

// "Open the Record Viewer" preference: on a fresh pill-open, jump straight to
// the first reference of the default type so the record (raw) view shows
// without the user clicking a row. Runs once per chat open and yields to:
//   - the references-table preference (the default — does nothing here),
//   - a deep link that targets a specific item (pendingItemHash) or a type
//     other than the auto-selected first one (an explicit ?type= the user
//     linked to), so shared/bookmarked links aren't overridden,
//   - an already-open detail view.
// Setting communicationGroups + currentGroupIndex + showDetailView mirrors the
// pendingItemHash watcher exactly; the URL-sync composable then reflects the
// opened item into ?item= just as a manual row click would.
const maybeAutoOpenRecordViewer = (data: ReferencesData) => {
    if (dialoguePreferencesStore.referencesPillBehavior !== 'record-viewer') return;
    const chat = selectedChat.value;
    if (!chat || autoOpenedChatId.value === chat.RecordId) return;
    // Respect deep links: an explicit item target, or a type the user linked
    // to that isn't the one we'd default to, means don't force the first ref.
    if (dataViewStore.pendingItemHash) return;
    if (dataViewStore.showDetailView) return;

    const type = selectedType.value;
    if (!type) return;
    const typeRefs = (data.references[type] || []) as Array<{ content_hash?: string }>;
    if (typeRefs.length === 0) return;

    autoOpenedChatId.value = chat.RecordId;
    dataViewStore.communicationGroups = typeRefs as never;
    dataViewStore.currentGroupIndex = 0;
    dataViewStore.showDetailView = true;
};

const fetchReferences = async () => {
    if (!selectedChat.value || !matterStore.currentMatter) {
        return;
    }

    referencesError.value = null;

    // Serve cached references synchronously when available. This is what makes
    // collapsing and re-expanding the references panel (which remounts this
    // component) instant instead of flashing a loading state and re-fetching —
    // references for a completed chat don't change.
    const cached = referenceUnitStore.getCachedReferences(selectedChat.value.RecordId);
    if (cached) {
        applyReferences(cached as ReferencesData);
        return;
    }

    referencesLoading.value = true;

    try {
        // Ensure metadata is loaded before fetching. The selectedChat watcher
        // can fire before the first metadata load resolves, and a matter with
        // no available data types never populates metadata at all. Previously
        // we bailed here when metadata was missing, which left the panel stuck
        // on its loading state forever (e.g. a query that found no references).
        if (!referenceUnitStore.hasMetadata) {
            await referenceUnitStore.loadMetadata(matterStore.currentMatter.RecordId);
        }

        const data = await referenceUnitStore.loadReferences(
            selectedChat.value.RecordId,
            matterStore.currentMatter.RecordId,
        );
        console.log('[ReferencesPanel] Loaded references:', data);
        applyReferences(data);
    } catch (err) {
        console.error('[ReferencesPanel] Error fetching references:', err);
        referencesError.value = err instanceof Error ? err.message : String(err);
        referencesLoading.value = false;
    }
};

watch(
    selectedChat,
    (newChat, oldChat) => {
        // Checked rows belong to one chat's references; drop them when the
        // chat actually changes (oldChat is undefined on the immediate mount
        // call, where clearing would wipe a collapse/re-expand survivor).
        if (oldChat !== undefined && newChat?.RecordId !== oldChat?.RecordId) {
            referenceSelectionStore.clearSelection();
            // New chat in view → re-arm the record-viewer auto-open.
            autoOpenedChatId.value = null;
        }
        if (newChat) {
            fetchReferences();
        } else {
            referencesData.value = null;
            selectedType.value = null;
        }
    },
    { immediate: true },
);

watch(selectedType, (newType, oldType) => {
    // Switching the displayed type swaps the whole row set out from under
    // the selection — clear it rather than keep invisible checked rows.
    if (oldType !== undefined && newType !== oldType) {
        referenceSelectionStore.clearSelection();
    }
    if (!newType) return;
    const units = referenceUnitStore.getAvailableReferenceUnits(newType);
    if (units.length === 0) return;
    // Only fall back to the default when the current view isn't valid for
    // the new type — otherwise a URL-supplied ?view= would be clobbered.
    const valid = units.some((u) => u.name === selectedReferenceUnit.value);
    if (!valid) {
        selectedReferenceUnit.value = units[0].name;
    }
});

// Cold-deep-link / back-forward: when ?item=<hash> is in the URL, the URL
// sync composable parks the hash on dataViewStore.pendingItemHash. Once
// references and the active type are loaded, open the inline detail view
// for the matching reference. We pass the unsorted per-type list, so the
// raw view's prev/next walks unsorted on cold load — clicking afterwards
// lets the type-specific factory take over with its own sort order.
watch([() => dataViewStore.pendingItemHash, referencesData, selectedType], () => {
    const hash = dataViewStore.pendingItemHash;
    if (!hash || !referencesData.value || !selectedType.value) return;
    const typeRefs = (referencesData.value.references[selectedType.value] || []) as Array<{
        content_hash?: string;
    }>;
    const idx = typeRefs.findIndex((r) => r.content_hash === hash);
    if (idx >= 0) {
        dataViewStore.communicationGroups = typeRefs as never;
        dataViewStore.currentGroupIndex = idx;
        dataViewStore.showDetailView = true;
    }
    dataViewStore.pendingItemHash = null;
});

watch(selectedReferenceUnit, (newUnit, oldUnit) => {
    // Same reasoning as the type watcher: a different reference unit means
    // different rows (different content hashes).
    if (oldUnit !== undefined && newUnit !== oldUnit) {
        referenceSelectionStore.clearSelection();
    }
    if (selectedChat.value && selectedType.value) {
        fetchReferences();
    }
});
</script>

<template>
    <div class="references-panel">
        <!-- Panel header: title + total count on the left, split (full-width)
             and close controls on the right. Always rendered so the header is
             stable across loading / error / empty states. -->
        <div class="references-panel-header">
            <div class="references-panel-title">
                <h2>{{ t('matters.references') }}</h2>
                <span
                    v-if="!referencesLoading && !referencesError && totalReferenceCount > 0"
                    class="references-panel-count"
                    >{{ totalReferenceCount }}</span
                >
            </div>
            <div class="references-panel-actions">
                <button
                    v-if="canCollapse"
                    type="button"
                    class="references-panel-icon-btn"
                    :title="t('matters.collapseReferences')"
                    @click="emit('collapse')"
                >
                    <span class="material-icons">remove</span>
                </button>
                <button
                    v-if="canToggleFullWidth"
                    type="button"
                    class="references-panel-icon-btn"
                    :title="
                        isFullWidth
                            ? t('references.exitFullWidth')
                            : t('references.enterFullWidth')
                    "
                    :aria-pressed="isFullWidth"
                    @click="emit('toggle-full-width')"
                >
                    <!-- Lucide fold-horizontal (when full-width → collapse back)
                         / unfold-horizontal (when split → expand). -->
                    <svg
                        v-if="isFullWidth"
                        class="references-split-icon"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path d="M2 12h6" />
                        <path d="M22 12h-6" />
                        <path d="M12 2v2" />
                        <path d="M12 8v2" />
                        <path d="M12 14v2" />
                        <path d="M12 20v2" />
                        <path d="m19 9-3 3 3 3" />
                        <path d="m5 15 3-3-3-3" />
                    </svg>
                    <svg
                        v-else
                        class="references-split-icon"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path d="M16 12h6" />
                        <path d="M8 12H2" />
                        <path d="M12 2v2" />
                        <path d="M12 8v2" />
                        <path d="M12 14v2" />
                        <path d="M12 20v2" />
                        <path d="m19 15 3-3-3-3" />
                        <path d="m5 9-3 3 3 3" />
                    </svg>
                </button>
                <button
                    type="button"
                    class="references-panel-icon-btn"
                    :title="t('matters.closeReferences')"
                    @click="emit('close')"
                >
                    <span class="material-icons">close</span>
                </button>
            </div>
        </div>

        <div v-if="referencesLoading" class="loading-state">
            <div class="loading-dots"><span></span><span></span><span></span></div>
        </div>
        <div v-else-if="referencesError" class="error-state">
            {{ t('references.errorPrefix', { error: referencesError }) }}
        </div>
        <div v-else-if="referencesData && dynamicTypeTabs.length > 0" class="references-panel-body">
            <div class="references-controls-row">
                <!-- View toggle (references table vs. raw). Rendered as the
                     layers glyph from the mockup; only shown when a type has
                     more than one view. -->
                <div
                    v-if="selectedType && availableReferenceUnits.length > 1"
                    class="references-view-toggle"
                >
                    <button
                        type="button"
                        class="references-panel-icon-btn references-view-toggle-btn"
                        :title="
                            getViewLabel(
                                availableReferenceUnits.find(
                                    (ru) => ru.name === selectedReferenceUnit,
                                ),
                            )
                        "
                        @click="toggleViewDropdown"
                    >
                        <span class="material-icons-outlined">layers</span>
                    </button>
                    <div v-if="isViewDropdownOpen" class="type-dropdown">
                        <div
                            v-for="ru in availableReferenceUnits"
                            :key="ru.name"
                            class="type-dropdown-option"
                            :class="{ active: ru.name === selectedReferenceUnit }"
                            @click="selectView(ru.name)"
                        >
                            <span class="material-icons-outlined">{{ getViewIcon(ru) }}</span>
                            <span class="option-text">{{ ru.description }}</span>
                        </div>
                    </div>
                </div>

                <!-- Type chips (single-select): one active type at a time,
                     each showing its reference count. Replaces the old type
                     dropdown. -->
                <div v-if="showTypeSelector" class="references-type-chips">
                    <button
                        v-for="typeName in dynamicTypeTabs"
                        :key="typeName"
                        type="button"
                        class="references-type-chip"
                        :class="{ 'is-active': typeName === selectedType }"
                        @click="selectType(typeName)"
                    >
                        <span class="references-type-chip-label">{{ typeName }}</span>
                        <span class="references-type-chip-count">{{
                            typeReferenceCounts[typeName]
                        }}</span>
                    </button>
                </div>
            </div>

            <ReferenceViewContainer
                v-if="selectedType"
                :key="selectedType"
                :type-name="selectedType"
                :references-data="referencesData"
            />

            <BulkActionsBar />
        </div>
        <div v-else class="empty-state">{{ t('references.noReferencesFound') }}</div>
    </div>
</template>
